/**
 * Draws the document's markups and takeoffs for one sheet over the pdf.js
 * canvas, with the shared core renderer (the same drawing code Centerline's
 * TakeoffDrawingAnnotation was ported into). pdf.js' viewport transform maps
 * PDF page space (y up, /Rotate, MediaBox origin) to the canvas, exactly like
 * PDFKit does for annotations on the Mac.
 */
import { useEffect, useRef, useState } from "react";
import { drawPage, type CenterlineDocument, type Ctx, type RenderEnv } from "@centerline/core/viewer";
import type { PageViewport } from "./pdf";
import { canvasScale, freeCanvas } from "./pdf";
import type { SharpRegion } from "./sharp";

/** While a crisp region is shown, the full-page markup layer only backs up panning, so keep it small (iPad memory). */
const BASE_WHEN_SHARP = 4_000_000;

const images = new Map<string, { image: HTMLImageElement; width: number; height: number } | "loading" | "failed">();

function useImageEnv(): [RenderEnv, number] {
  const [tick, setTick] = useState(0);
  const env = useRef<RenderEnv>({
    image(b64) {
      const hit = images.get(b64);
      if (hit && hit !== "loading" && hit !== "failed") return hit;
      if (!hit) {
        images.set(b64, "loading");
        const img = new Image();
        img.onload = () => { images.set(b64, { image: img, width: img.naturalWidth, height: img.naturalHeight }); setTick((t) => t + 1); };
        img.onerror = () => images.set(b64, "failed");
        const head = atob(b64.slice(0, 16));
        img.src = `data:${head.startsWith("\x89PNG") ? "image/png" : "image/jpeg"};base64,${b64}`;
      }
      return null;
    },
  });
  return [env.current, tick];
}

export function MarkupCanvas({ doc, pageId, viewport, visibleLayers, skip, sharp }: {
  doc: CenterlineDocument; pageId: string; viewport: PageViewport; visibleLayers: readonly string[]; skip?: ReadonlySet<string>;
  /** Region the sheet's sharp-zoom overlay covers; markups there are redrawn at that resolution. */
  sharp?: (SharpRegion & { vpScale: number }) | null;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const sharpRef = useRef<HTMLCanvasElement>(null);
  const [env, imagesLoaded] = useImageEnv();
  const layerKey = visibleLayers.join("\u0000");
  const reg = sharp && Math.abs(sharp.vpScale - viewport.scale) < 1e-9 && sharp.x + sharp.w <= viewport.width + 2 && sharp.y + sharp.h <= viewport.height + 2 ? sharp : null;
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let out = canvasScale(viewport.width, viewport.height); // same budget as the sheet (iOS caps canvases ~16.7 MP)
    if (reg) out = Math.min(out, Math.sqrt(BASE_WHEN_SHARP / (viewport.width * viewport.height)));
    const w = Math.max(1, Math.floor(viewport.width * out)), h = Math.max(1, Math.floor(viewport.height * out));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    const ctx = canvas.getContext("2d")!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const [a, b, c, d, e, f] = viewport.transform as number[];
    ctx.setTransform(a! * out, b! * out, c! * out, d! * out, e! * out, f! * out);
    drawPage(ctx as unknown as Ctx, doc, pageId, doc.pageScales[pageId], visibleLayers, env, skip);
    // The crisp copy covers this area; clear it here so the soft copy doesn't show as a halo around it.
    if (reg) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(Math.ceil(reg.x * out), Math.ceil(reg.y * out), Math.floor(reg.w * out) - 1, Math.floor(reg.h * out) - 1); }
    // Only redraw when what's drawn changes (doc = markups/measurements/scales, page, zoom, layers, images, crisp region),
    // not on every Viewer state update (hover, loupe, pointer moves). eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, pageId, viewport, layerKey, skip, imagesLoaded, reg]);
  useEffect(() => {
    const canvas = sharpRef.current;
    if (!canvas) return;
    if (!reg) { if (canvas.width) freeCanvas(canvas); canvas.style.display = "none"; return; }
    const s = reg.scale;
    const w = Math.max(1, Math.round(reg.w * s)), h = Math.max(1, Math.round(reg.h * s));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    Object.assign(canvas.style, { display: "block", left: `${reg.x}px`, top: `${reg.y}px`, width: `${reg.w}px`, height: `${reg.h}px` });
    const ctx = canvas.getContext("2d")!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const [a, b, c, d, e, f] = viewport.transform as number[];
    ctx.setTransform(a! * s, b! * s, c! * s, d! * s, (e! - reg.x) * s, (f! - reg.y) * s);
    drawPage(ctx as unknown as Ctx, doc, pageId, doc.pageScales[pageId], visibleLayers, env, skip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, pageId, viewport, layerKey, skip, imagesLoaded, reg]);
  useEffect(() => () => { if (sharpRef.current) freeCanvas(sharpRef.current); }, []);
  return <>
    <canvas ref={ref} className="markup-canvas" data-testid="markup-canvas" />
    <canvas ref={sharpRef} className="markup-sharp" data-testid="markup-sharp" style={{ display: "none" }} />
  </>;
}
